import { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSubscription } from '@/hooks/useSubscription';
import { PRICING } from '@/lib/subscription';
import { toast } from 'sonner';

const freeFeatures = [
  '1 importación de extracto bancario (PDF)',
  'Semáforo y FOCO Score básico',
  'Evaluación básica de salud financiera',
  'Panel principal y transacciones',
];

const premiumFeatures = [
  'Importaciones PDF ilimitadas',
  'Bola de nieve y metas',
  'Topes de gastos con alertas',
  'Panel anual completo',
  'Notificaciones email',
  'Detalle completo FOCO Score',
  'Chat con asesoría IA',
];

export default function Upgrade() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPremium, startCheckout, checkoutLoading } = useSubscription();

  useEffect(() => {
    if (searchParams.get('checkout') === 'cancelled') {
      toast.info('Pago cancelado. Puedes intentarlo cuando quieras.');
    }
  }, [searchParams]);

  useEffect(() => {
    if (isPremium) {
      navigate('/dashboard', { replace: true });
    }
  }, [isPremium, navigate]);

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-8 py-4">
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold flex items-center justify-center gap-2">
            <Sparkles className="h-8 w-8 text-primary" />
            FOCO AI Premium
          </h1>
          <p className="text-muted-foreground">
            Toma el control total de tus finanzas personales
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Gratuito</CardTitle>
              <CardDescription>$0 — para empezar</CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="h-4 w-4 text-muted-foreground shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-primary shadow-md">
            <CardHeader>
              <CardTitle className="text-primary">Premium</CardTitle>
              <CardDescription>Todo incluido</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2 text-sm">
                {premiumFeatures.map((f) => (
                  <li key={f} className="flex gap-2">
                    <Check className="h-4 w-4 text-success shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <Card className="border-primary">
          <CardHeader className="pb-2 text-center">
            <CardTitle className="text-lg">Premium</CardTitle>
            <p className="text-3xl font-bold">{PRICING.monthly.label}</p>
            <p className="text-sm text-muted-foreground">Facturación mensual · cancela cuando quieras</p>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              size="lg"
              onClick={() => startCheckout('monthly')}
              disabled={checkoutLoading || isPremium}
            >
              {isPremium ? 'Plan activo' : 'Activar Premium'}
            </Button>
          </CardContent>
        </Card>

        <p className="text-xs text-center text-muted-foreground">
          Pagos seguros procesados por Stripe en USD. Cancela cuando quieras desde Configuración. Al suscribirte
          aceptas los{' '}
          <Link to="/terms-and-conditions" className="underline hover:text-foreground">
            Términos y Condiciones
          </Link>{' '}
          y la{' '}
          <Link to="/privacy-policy" className="underline hover:text-foreground">
            Política de Privacidad
          </Link>
          .
        </p>
      </div>
    </AppLayout>
  );
}
